// =====================================================================
// src/pages/intl-acca/PassLandingPage.tsx
// =====================================================================
// Tournament Pass purchase surface (PRD §6.5, PRD §4 Stream 1).
// Public — works without auth. Renders PRE_TOURNAMENT and ACTIVE
// tournaments side-by-side; falls back to the "not on sale" state
// when pass_active=false (the founder-controlled default).
//
// Routes mounted at: /intl-acca/pass (with optional ?tournament=<id>)

import type {
  IntlAccaTournament,
  IntlAccaPassPurchase,
} from "../../db/intl-acca-types";
import { PassPurchaseCard } from "../../components/intl-acca/PassPurchaseCard";
import { HonestEmptyState } from "../../components/intl-acca/HonestEmptyState";

export interface PassLandingPageProps {
  loading: boolean;
  error: string | null;
  /** All tournaments with pass_active=true (or in PRE_TOURNAMENT / ACTIVE). */
  availableTournaments: IntlAccaTournament[];
  /** User's existing passes (for the "already held" states). */
  userPasses: IntlAccaPassPurchase[];
  /** True when the viewer has Sika Pro+ — all passes are comp. */
  complimentaryViaSubscription: boolean;
  onPurchase?: (tournamentId: string) => void;
  onSubscribe?: () => void;
}

export function PassLandingPage(props: PassLandingPageProps): JSX.Element {
  const {
    loading, error, availableTournaments, userPasses,
    complimentaryViaSubscription, onPurchase, onSubscribe,
  } = props;

  if (loading) {
    return (
      <div className="ia-root ia-page-pad" aria-busy="true">
        <div className="ia-eyebrow">Loading pass options…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ia-root ia-page-pad">
        <HonestEmptyState variant="custom" title="Pass page error." body={error} />
      </div>
    );
  }

  const passByTournament = new Map(userPasses.map((p) => [p.tournament_id, p]));
  const onSaleNow = availableTournaments.filter((t) => t.pass_active);
  const dormant = availableTournaments.filter((t) => !t.pass_active);

  return (
    <div className="ia-root">
      {/* Hero */}
      <section
        style={{
          padding: "80px var(--sz-space-7)",
          borderBottom: "1px solid var(--border-faint)",
        }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div className="ia-eyebrow">International Acca · Tournament Pass</div>
          <h1
            className="ia-title"
            style={{
              fontSize: "var(--sz-head-xl)",
              marginTop: 18,
              lineHeight: 0.98,
              maxWidth: 900,
            }}
          >
            One pass. <em>Every signal.</em> Every reasoning note.
          </h1>
          <p className="ia-sub" style={{ marginTop: 22, fontSize: "var(--sz-body-lg)", maxWidth: 700 }}>
            The Tournament Pass unlocks STRIKZA's dedicated pricing engine for
            the duration of a specific international window. Pass expires at
            tournament end — no rollover, no auto-renew (PRD §12).
          </p>
        </div>
      </section>

      {/* On-sale grid */}
      <section className="ia-page-pad">
        <div className="ia-eyebrow" style={{ marginBottom: 18 }}>
          On sale now
        </div>
        {onSaleNow.length === 0 ? (
          <HonestEmptyState
            variant="custom"
            title="No passes on sale right now."
            body="The founder activates pass_active per tournament via the admin RPC. Subscribe to Sika Pro to auto-receive every pass as it activates."
            actionLabel="See Sika Pro"
            onAction={onSubscribe}
          />
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${Math.min(onSaleNow.length, 3)}, 1fr)`,
              gap: 20,
            }}
          >
            {onSaleNow.map((t, i) => (
              <PassPurchaseCard
                key={t.id}
                tournament={t}
                existingPass={passByTournament.get(t.id) ?? null}
                complimentaryViaSubscription={complimentaryViaSubscription}
                onPurchase={() => onPurchase?.(t.id)}
                primary={i === 0}
              />
            ))}
          </div>
        )}
      </section>

      {/* Dormant — upcoming windows */}
      {dormant.length > 0 ? (
        <section className="ia-page-pad" style={{ paddingTop: 0 }}>
          <div className="ia-eyebrow" style={{ marginBottom: 18 }}>
            Upcoming · Not yet on sale
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 20,
            }}
          >
            {dormant.map((t) => (
              <PassPurchaseCard
                key={t.id}
                tournament={t}
                existingPass={passByTournament.get(t.id) ?? null}
              />
            ))}
          </div>
        </section>
      ) : null}

      {/* Subscription cross-sell */}
      {!complimentaryViaSubscription && onSubscribe ? (
        <section
          className="ia-page-pad"
          style={{
            paddingTop: 0,
            paddingBottom: 64,
          }}
        >
          <div
            style={{
              padding: 24,
              background: "var(--bg-elev)",
              border: "1px solid var(--border)",
              borderRadius: "var(--sz-radius-lg)",
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 24,
              alignItems: "center",
            }}
          >
            <div>
              <div className="ia-eyebrow">Or subscribe</div>
              <h3 className="ia-title" style={{ fontSize: "var(--sz-head-sm)", marginTop: 8 }}>
                Sika Pro — every pass included.
              </h3>
              <p className="ia-sub" style={{ marginTop: 6, fontSize: "var(--sz-body-sm)" }}>
                Year-round STRIKZA. Every Tournament Pass automatically comp'd
                via public.intl_acca_record_pass(is_complimentary=true).
              </p>
            </div>
            <button
              type="button"
              onClick={onSubscribe}
              style={{
                background: "transparent",
                color: "var(--text)",
                border: "1px solid var(--border-strong)",
                borderRadius: "var(--sz-radius-md)",
                padding: "14px 22px",
                fontFamily: "var(--sz-font-mono)",
                fontSize: 11,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Subscribe →
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
}

// =====================================================================
// DATA BINDING CONTRACT
// =====================================================================
//
// const { data: tournaments } = await supabase.from('intl_acca_tournaments')
//                                     .select('*')
//                                     .in('status', ['PRE_TOURNAMENT', 'ACTIVE'])
//                                     .order('start_date');
//
// const { data: userPasses }  = await supabase.from('intl_acca_pass_purchases')
//                                     .select('*').eq('user_id', userId);
//
// const complimentaryViaSubscription = tierMeets(currentTier, 'sika_pro');
//
// onPurchase: host wires to Paystack checkout. The verified callback
// calls public.intl_acca_record_pass(...) via service_role — never
// from this component.
